"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useApi } from "@/lib/api"
import type { AutomationEmailsResponse } from "@/types/automation-email"
import { logger } from "@/lib/logger"

/**
 * Fetches automation-sent emails for a record. Passes entityType through to the BE so
 * Group C tabs (VmsRequest / ClientRequest / AtsPlacementRequest / PreOnboardRequest)
 * resolve their automation logs correctly — the default Assignment branch on the BE
 * fans out to WorkOrder versions, which yields zero results for entities that don't
 * have versions. Mirrors the entityType-aware pattern already used by useAuditHistory.
 *
 * @param itemId Record id to fetch emails for.
 * @param isActive True when the Automation Emails tab is the active tab.
 * @param isPanelOpen True when the parent panel is open.
 * @param entityType Optional entity-type override. Omit (or pass 'Assignment') to keep
 *   the legacy WorkOrder-version fan-out behavior the BE defaults to.
 */
export function useAutomationEmails(itemId: string, isActive: boolean, isPanelOpen: boolean, entityType?: string) {
  const { workflowApi } = useApi()
  const [emails, setEmails] = useState<AutomationEmailsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasFetchedRef = useRef(false)

  const fetchEmails = useCallback(async () => {
    if (!itemId) return
    setLoading(true)
    setError(null)

    try {
      // Only append entityType when it's set AND not the default Assignment — keeps the
      // URL shape clean for the legacy WO/Assignment case and matches what useAuditHistory
      // does on the sibling endpoint.
      const params = new URLSearchParams()
      if (entityType && entityType !== 'Assignment') {
        params.append('entityType', entityType)
      }
      const queryString = params.toString()
      const url = `/api/Workflow/v2/automation-emails/${itemId}${queryString ? '?' + queryString : ''}`

      const response = await workflowApi.get<AutomationEmailsResponse>(url)
      setEmails(response)
      hasFetchedRef.current = true
    } catch (err) {
      if (err instanceof Error) logger.error(err, { hook: 'useAutomationEmails', itemId, entityType })
      setError(err instanceof Error ? err.message : "Failed to fetch automation emails")
      setEmails(null)
    } finally {
      setLoading(false)
    }
  }, [itemId, entityType, workflowApi])

  useEffect(() => {
    // Reset cache when panel is closed or itemId changes
    if (!isPanelOpen || !itemId) {
      hasFetchedRef.current = false
      setEmails(null)
      setError(null)
      return
    }

    // Only auto-fetch if we haven't fetched for this item yet and automation tab is active
    if (!hasFetchedRef.current && isActive) {
      fetchEmails()
    }
  }, [itemId, isActive, isPanelOpen, fetchEmails])

  const flattenedEmails = emails?.workOrders?.flatMap(wo => wo.emails) || []

  return {
    workOrders: emails?.workOrders || [],
    emails: flattenedEmails,
    loading,
    error,
    refresh: fetchEmails,
  }
}
